import { useState } from 'react';
import { sideNavItemClassName } from '@servicecompressionllc/sc-design-system';
import { ChevronDown, ChevronRight, X } from '@servicecompressionllc/sc-design-system/icons';
import { formatAgo } from '../../../lib/format.js';
import { displayName } from './PeoplePicker.js';
import { useSharedWithMe } from './useSharedWithMeLogic.js';

const COLLAPSED_KEY = 'sc-platform:chat:sharedCollapsed';
/** Shares shown before "Show all". */
const VISIBLE_SHARES = 5;

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

interface SharedWithMeSectionProps {
  enabled: boolean;
  activeShareId: string | undefined;
  onOpen: (shareId: string) => void;
}

/** Conversations others shared with the caller, above Recent. Hidden when there are none. */
export const SharedWithMeSection = ({
  enabled,
  activeShareId,
  onOpen,
}: SharedWithMeSectionProps) => {
  const { list, hide } = useSharedWithMe(enabled);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [showAll, setShowAll] = useState(false);
  const shares = list.data?.shares ?? [];
  if (shares.length === 0) return null;
  const unread = list.data?.unreadCount ?? 0;
  const visible = showAll ? shares : shares.slice(0, VISIBLE_SHARES);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      // A per-browser convenience; the section still works without it.
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className="flex items-center gap-1.5 rounded px-1 pt-2 text-left"
      >
        {collapsed ? (
          <ChevronRight size={12} aria-hidden className="text-ink-subtle" />
        ) : (
          <ChevronDown size={12} aria-hidden className="text-ink-subtle" />
        )}
        <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
          Shared with me
        </span>
        {unread > 0 && (
          <span
            aria-label={`${String(unread)} unread`}
            className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-[5px] text-[11px] font-semibold text-white"
          >
            {unread}
          </span>
        )}
      </button>
      {!collapsed && (
        <>
          {visible.map((s) => {
            const active = s.shareId === activeShareId;
            const showUnread = s.unread && !active;
            return (
              <div key={s.shareId} className="group flex items-start gap-0.5">
                <button
                  type="button"
                  onClick={() => onOpen(s.shareId)}
                  className={`${sideNavItemClassName(active)} flex min-w-0 flex-1 items-start gap-2 text-left`}
                >
                  <span
                    aria-label={showUnread ? 'Unread' : undefined}
                    className={`mt-1.5 h-2 w-2 flex-none rounded-full ${showUnread ? 'bg-primary' : ''}`}
                  />
                  <span className="flex min-w-0 flex-col leading-snug">
                    <span className={`truncate ${showUnread ? 'font-semibold' : ''}`}>
                      {s.title}
                    </span>
                    <span className={`text-xs ${active ? 'text-white/75' : 'text-ink-subtle'}`}>
                      {displayName(s.sharedBy)} · {formatAgo(s.sharedAt)}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${s.title} from your list`}
                  onClick={() => hide.mutate(s.shareId)}
                  disabled={hide.isPending}
                  className="mt-1 rounded p-1 text-ink-muted opacity-100 transition-opacity hover:bg-surface-hover hover:text-ink focus-visible:opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                >
                  <X size={15} aria-hidden />
                </button>
              </div>
            );
          })}
          {shares.length > VISIBLE_SHARES && (
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              className="rounded-md px-2 py-1 text-left text-[11px] font-medium text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink"
            >
              {showAll ? 'Show fewer' : `Show all (${String(shares.length)})`}
            </button>
          )}
        </>
      )}
    </>
  );
};
